/**
 * UpdateLinkModal.tsx - Fundraiser Link Management Modal
 *
 * This component provides an interface for managing and updating fundraiser
 * links including custom URLs, promoter links, and sharing configurations.
 *
 * Key Features:
 * - Custom fundraiser URL management
 * - Promoter-specific link generation
 * - Link validation and availability checking
 * - Bulk link updates for promoters
 * - Link copying and sharing functionality
 * - URL format validation and suggestions
 *
 * Link Management:
 * - Base fundraiser URL customization
 * - Promoter parameter integration (?PID=)
 * - Domain and subdomain options
 * - Link preview and testing
 * - Bulk promoter link updates
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - fundraiser: Fundraiser - Fundraiser to manage links for
 * - hasPromoters: boolean - Whether fundraiser has promoters
 * - onLinkCopied: (message: string) => void - Link copy success handler
 * - onLinkUpdatedAndCopied: (message: string) => void - Update and copy handler
 *
 * Dependencies: Fundraiser types, URL validation, clipboard API
 */

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X } from "lucide-react";
import { AuthService } from "@mindmemobile/mm-shared-global";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import { IUpdateFundraiserURLRequestModel } from "../interfaces/IGalleryModels";
import { CreateFundraiserModel } from "../models";
import { fundraiserStore } from "../stores";
import ToastService from "./ToastService";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div<{ hasWarning: boolean }>`
  background: #ffffff;
  border-radius: 24px;
  padding: 40px;
  width: 90%;
  max-width: 500px;
  max-height: ${(props) => (props.hasWarning ? "650px" : "550px")};
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #dc2626;
  margin: 0;
  flex: 1;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    transform: scale(1.05);
  }
`;

const ModalText = styled.p`
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
  font-size: 16px;
`;

const URLInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const DomainSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #f8fafc;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const NameInputContainer = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
`;

const NameInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }
`;

const URLPreview = styled.div<{ hasError?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  padding: 8px 0;
  margin-top: 10px;
  font-size: 12px;
  text-align: left;
  margin-bottom: 20px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  color: ${(props) => (props.hasError ? "#dc2626" : "#64748b")};
  word-break: break-all;
  z-index: 10;
`;

const YellowWarningText = styled.div`
  background: #fef2f2;
  border: 1px solid #dc2626;
  border-radius: 12px;
  padding: 16px;
  margin: 60px 0 32px 0;
  color: #dc2626;
  font-weight: 600;
  text-align: center;
  font-size: 16px;
  line-height: 1.4;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ variant?: "danger" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "danger"
      ? `
    background: #dc2626;
    color: #ffffff;

    &:hover {
      background: #b91c1c;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

interface UpdateLinkModalProps {
  isVisible: boolean;
  onClose: (refresh?: boolean) => void;
  fundraiser: CreateFundraiserModel;
  hasPromoters: boolean;
  onLinkCopied: (message: string) => void;
  onLinkUpdatedAndCopied: (message: string) => void;
}

export const UpdateLinkModal: React.FC<UpdateLinkModalProps> = ({
  isVisible,
  onClose,
  fundraiser,
  hasPromoters,
  onLinkCopied,
  onLinkUpdatedAndCopied,
}) => {
  const domain = new URL(fundraiserStore.getFundRaiserDomainBaseURL()).hostname;
  const [selectedDomain, setSelectedDomain] = useState(domain);
  const [originalDomain, setOriginalDomain] = useState(domain);
  const [fundraiserName, setFundraiserName] = useState(
    fundraiser.pageName || ""
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [urlError, setUrlError] = useState(false);

  useEffect(() => {
    if (isVisible && fundraiser) {
      const formattedName = formatNameForUrl(fundraiser.pageName);
      setFundraiserName(formattedName);
      setSelectedDomain(domain);
      setOriginalDomain(domain);
      setHasChanges(false);
      setUrlError(false);
    }
  }, [isVisible, fundraiser]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  React.useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    }
  }, [isVisible, fundraiser]);
  const formatNameForUrl = (name: string) => {
    return name.toLowerCase().replace(/\s+/g, "-");
  };
  const fullUrl = `https://${selectedDomain}/fundraiser/${AuthService.shortUniqueId}/${fundraiserName}`;
  const validateUrl = (name: string) => {
    const invalidChars = /[^a-zA-Z0-9\-\.&?]/;
    return !invalidChars.test(name);
  };

  const handleNameChange = (value: string) => {
    setFundraiserName(value);
    const isValid = validateUrl(value);
    setUrlError(!isValid);
    setHasChanges(
      value !== fundraiser.pageName || selectedDomain !== originalDomain
    );
  };

  const handleDomainChange = (value: string) => {
    setSelectedDomain(value);
    setHasChanges(
      value !== originalDomain || fundraiserName !== fundraiser.pageName
    );
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const handleCopyLinkToClipboard = () => {
    copyToClipboard(fullUrl);
    onClose();
    onLinkCopied("Share link copied to clipboard");
  };

  const handleUpdateAndCopy = async () => {
    if (!urlError) {
      try {
        const requestModel: IUpdateFundraiserURLRequestModel = {
          id: fundraiser.id || "",
          pageName: fundraiserName,
          accountId: AuthService.authData.accountId || "",
          userId: AuthService.mindmeAccountDetails?.userId || "",
        };

        const response = await firstValueFrom(
          FundraiserService.updateFundraiserPageName(requestModel)
        );
        if (response.result.success) {
          onClose(true);
          copyToClipboard(fullUrl);
          ToastService.showSuccess("Link updated and copied successfully");
        } else if (response.result.failureReason === "AlreadyExists") {
          ToastService.showError("Link already exists");
        } else {
          ToastService.showError("Error updating link");
        }
      } catch (error) {
        console.error("Error updating link", error);
      }
    }
  };

  const handleCancelUpdateLink = () => {
    onClose();
  };

  const shouldShowWarning = hasChanges && hasPromoters;

  if (!isVisible) return null;

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent hasWarning={shouldShowWarning}>
        <ModalHeader>
          <ModalTitle style={{ color: "#1e293b" }}>Update Link</ModalTitle>
        </ModalHeader>

        <CloseButton onClick={() => onClose()}>
          <X size={20} />
        </CloseButton>

        <ModalText>
          This is a general link for the fundraiser. Share a Promoter's link to
          attribute donations to that Promoter.
        </ModalText>

        <URLInputsContainer>
          <DomainSelect
            value={selectedDomain}
            onChange={(e) => handleDomainChange(e.target.value)}
          >
            <option value={domain}>{domain}</option>
          </DomainSelect>

          <NameInputContainer>
            <NameInput
              type="text"
              value={fundraiserName}
              onChange={(e) => handleNameChange(e.target.value)}
              hasError={urlError}
              placeholder="fundraiser-name"
            />
            <URLPreview hasError={urlError}>{fullUrl}</URLPreview>
          </NameInputContainer>
        </URLInputsContainer>

        {shouldShowWarning && (
          <YellowWarningText>
            Changing the Fundraiser URL will also change individual Promoter
            URL's.
          </YellowWarningText>
        )}

        <ModalActions
          style={{
            marginTop: shouldShowWarning ? "24px" : "64px",
            justifyContent: "center",
          }}
        >
          {!hasChanges ? (
            <>
              <ModalButton onClick={handleCancelUpdateLink}>Close</ModalButton>
              <ModalButton
                onClick={handleCopyLinkToClipboard}
                style={{
                  background: "#3b82f6",
                  color: "#ffffff",
                }}
              >
                Copy
              </ModalButton>
            </>
          ) : (
            <>
              <ModalButton onClick={handleCancelUpdateLink}>Cancel</ModalButton>
              <ModalButton
                onClick={handleUpdateAndCopy}
                disabled={urlError}
                style={{
                  background: urlError ? "#9ca3af" : "#dc2626",
                  color: "#ffffff",
                  cursor: urlError ? "not-allowed" : "pointer",
                }}
              >
                Update & Copy
              </ModalButton>
            </>
          )}
        </ModalActions>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
